<template>
  <v-dialog
    v-model="modal"
    max-width="600"
    persistent
    @keydown.esc="close"
  >
    <template #activator="{ on, attrs }">
      <span v-bind="attrs" v-on="on">
        <slot name="default"/>
      </span>
    </template>
    <v-card>
      <v-card-title :class="`py-2 ${color} white--text`">{{ title }}</v-card-title>
      <v-form
        lazy-validation
        ref="form"
        class="d-flex flex-row pt-3 px-2 grey lighten-4"
        @submit.prevent="saveTag"
      >
        <v-text-field
          v-model="tagForm.name"
          outlined
          dense
          clearable
          required
          type="text"
          max-length="255"
          :label="setLabelName('name')"
          :disabled="submitLoading"
          :rules="[rules.required]"
        />
        <v-btn
          dark
          type="submit"
          elevation="0"
          :disabled="submitLoading"
          :color="tagForm.index > -1 ? 'green' : 'blue'"
          class="add-button"
        >{{ addButtonLabel(tagForm.index) }}</v-btn>
      </v-form>
      <v-divider />
      <v-simple-table>
        <v-progress-linear
          v-if="loading"
          absolute
          indeterminate
        />
        <thead>
          <tr>
            <th>{{ setLabelName('name') }}</th>
            <th class="text-center" width="150">
              <v-icon small>mdi-cog</v-icon>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(tag, index) in tags"
            :key="index"
          >
            <td>{{ tag }}</td>
            <td class="text-center">
              <v-btn
                icon
                color="green"
                :disabled="submitLoading"
                @click.prevent="editTag(index)"
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
              <v-btn
                icon
                color="red"
                :disabled="submitLoading"
                @click.prevent="deleteTag(index)"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </td>
          </tr>
        </tbody>
      </v-simple-table>
      <v-divider/>
      <v-card-actions>
        <v-spacer/>
        <v-btn
          dark
          elevation="0"
          :color="color"
          :disabled="loading"
          :loading="submitLoading"
          @click.prevent="save"
        >{{ saveButton }}</v-btn>
        <v-btn
          light
          elevation="0"
          @click.prevent="close"
        >{{ closeButton }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import Vue from 'vue';
import Http, { HttpError } from '@/utils/Http';
import { VForm } from 'vuetify/types/custom';
import { empty } from '@/utils/helpers';
import intl from '@/utils/intl';
import validators from '@/utils/validators';

export default Vue.extend({
  name: 'TagsModal',
  mixins: [validators],
  props: {
    color: {
      type: String,
      default: 'primary'
    },
    title: {
      type: String
    }
  },
  data() {
    return {
      modal: false,
      loading: false,
      tags: [] as string[],
      tagForm: {
        index: -1,
        name: null as string | null
      },
      submitLoading: false
    };
  },
  computed: {
    closeButton(): string {
      return intl.t('buttons.close');
    },
    saveButton(): string {
      return intl.t('buttons.save');
    }
  },
  watch: {
    async modal(status): Promise<void> {
      if (status) {
        await this.loadTags();
      }
    }
  },
  methods: {
    addButtonLabel(index: number): string {
      return index !== -1 ? intl.t('buttons.update') : intl.t('buttons.add');
    },
    setLabelName(name: string): string {
      return intl.t(`views.tags.${name}Label`);
    },
    async loadTags(): Promise<void> {
      this.loading = true;
      this.tags = [];
      try {
        const response = await (new Http()).get(`/v1/invoices/tags`);
        if (!empty(response.data)) {
          this.tags = response.data;
        }
      } catch (error) {
        const err = (error as HttpError).response?.data.error;
        this.$root.$children[0].$children[0].setFlash('error', err.message);
      }
      this.loading = false;
    },
    saveTag(): void {
      if ((this.$refs.form as VForm).validate()) {
        if (this.tagForm.index !== -1) {
          this.$set(this.tags, this.tagForm.index, this.tagForm.name);
        } else {
          this.tags.push(String(this.tagForm.name));
        }
        this.resetForm();
      }
    },
    editTag(index: number): void {
      this.tagForm = {
        index,
        name: this.tags[index]
      };
    },
    deleteTag(index: number): void {
      this.tags.splice(index, 1);
    },
    resetForm(): void {
      (this.$refs.form as VForm).reset();
      this.tagForm = {
        index: -1,
        name: null
      };
    },
    close(): void {
      this.modal = false;
      this.resetForm();
    },
    async save(): Promise<void> {
      this.submitLoading = true;
      try {
        const tagsToSave = this.tags.length > 0 ? this.tags : ["NULL"];
        await (new Http()).put(`/v1/invoices/tags`, JSON.stringify(tagsToSave));
        this.$root.$children[0].$children[0].setFlash('success', intl.t('views.tags.saveMessage'));
        this.close();
      } catch (error) {
        const err = (error as HttpError).response?.data.error;
        this.$root.$children[0].$children[0].setFlash('error', err.message);
      }
      this.submitLoading = false;
    }
  }
});
</script>

<style lang="scss" scoped>
.add-button {
  margin-top: 2px;
  margin-left: 12px;
}
</style>
